import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { UserService } from '../../../../services/user.service';
import { ListService } from '../../../../shared/services/list.service';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { CommonModule } from '@angular/common';
import { AuthorizeButtonDirective } from '../../../../shared/authorization/authorizeButton.directive';
import { ButtonComponent } from '@ppl/ui';
import { ListTableComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { PurchaseRequestFiltersComponent } from '../purchase-request-filters/purchase-request-filters.component';
import { CreatePurchaseRequestComponent } from '../create-purchase-request/create-purchase-request.component';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'ppl-purchase-request-staff-view',
  standalone: true,
  imports: [
    ListTableComponent,
    ButtonComponent,
    NgZorroModule,
    CommonModule,
    PurchaseRequestFiltersComponent,
    AuthorizeButtonDirective,
    CreatePurchaseRequestComponent
  ],
  templateUrl: './purchase-request-staff-view.component.html',
  styleUrl: './purchase-request-staff-view.component.css'
})
export class PurchaseRequestStaffViewComponent {
  @ViewChild('viewPurchaseRequestDialog') viewPurchaseRequestDialog!: TemplateRef<any>;
  @ViewChild('createPurchaseRequestDialog') createPurchaseRequestDialog!: TemplateRef<any>;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;

  private dialogSvc = inject(DialogService);

  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  actionsToBeHidden: string[] = [];
  addDialogId: string = '';
  addViewPurchaseRequestDialogId: any;
  appliedFilters: any[] = [];
  chequeNumbers: string[] = [];
  columnsToBeHidden: string[] = [];
  isPageRestrictionLoaded: boolean = false;
  isReadOnly: boolean = false;
  mode: 'add' | 'edit' | 'view' | 'actions' = 'add';
  pageId: string = PAGE_CONSTANTS.STAFF_PURCHASE_REQUESTS;
  programName: string = '';
  purchaseRequests: any[] = [];
  viewPurchaseRequestData: any;

  cols: any = [
    {
      field: 'pplPurchaseId',
      header: 'PPL Purchase Id',
      jsonpath: 'pplPurchaseId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'crmNumber',
      header: 'CRM Case Number',
      jsonpath: 'crmNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerName',
      header: 'Participant Name',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'participantPplId',
      header: 'Participant PPL ID',
      jsonpath: 'participantPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'service',
      header: 'Service',
      jsonpath: 'service',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n'
    },
    {
      field: 'vendorName',
      header: 'Vendor Name',
      jsonpath: 'vendorName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalAmountDisplay',
      header: 'Total Amount',
      jsonpath: 'totalAmount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n'
    },
    {
      field: 'status',
      header: 'Status',
      jsonpath: 'status',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'dateLastUpdated',
      header: 'Updated Date',
      jsonpath: 'dateLastUpdated',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  constructor(
    private userService: UserService,
    private listService: ListService,
    private allowListStore: AllowListAuthStore
  ) {
  }

  private formatCurrency(amount: any): string {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }
    const num = Number(amount);
    if (isNaN(num)) {
      return amount;
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }

  ngOnInit() {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.userService.setLeadEntry('purchaserequest');
    this.setPageAccess();
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    if (pageConfig) {
      const allowedActions = this.allowListStore.getAllowedActions(this.pageId);
      const allowedButtons = this.allowListStore.getAllowedButtons(this.pageId);
      this.isReadOnly = allowedActions.length === 0 && allowedButtons.length === 0;
    } else {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }

  getFilters(event: any) {
    const defaultDateFilter = new Date();
    defaultDateFilter.setDate(defaultDateFilter.getDate() - 30); // subtract days

    const filters = [];
    filters.push({
      jsonPath: "serviceDate",
      value: event.Timeframe ? event.Timeframe : defaultDateFilter.toLocaleDateString('en-US'),
      operator: "greaterThan"
    });

    if (event.consumer && event.consumer.trim()) {
      filters.push({
        jsonPath: "consumerName",
        value: event.consumer.trim(),
        operator: "Contains"
      });
    }

    if (event.consumerPplId && event.consumerPplId.trim()) {
      filters.push({
        jsonPath: "participantPplId",
        value: event.consumerPplId.trim(),
        operator: "Equal"
      });
    }

    if (event.pplPurchaseId && event.pplPurchaseId.trim()) {
      filters.push({
        jsonPath: "pplPurchaseId",
        value: event.pplPurchaseId.trim(),
        operator: "Equal"
      });
    }

    if (event.status) {
      filters.push({
        jsonPath: "status",
        value: event.status,
        operator: "Equal"
      });
    }

    if (event.serviceCode) {
      filters.push({
        jsonPath: "service_code",
        value: event.serviceCode,
        operator: "Equal"
      });
    }

    if (event.crmNumber && event.crmNumber.trim()) {
      filters.push({
        jsonPath: "crmNumber",
        value: event.crmNumber.trim(),
        operator: "Equal"
      });
    }

    return filters;
  }

  onSearch(event: any) {
    const filters = this.getFilters(event);
    this.appliedFilters = filters;
    this.searchPurchaseRequests(filters, undefined, undefined, event.chequeLookUp, this.childComponent?.recordsPerPage ?? 20);
  }

  onExport(event: any) {
    const filters: any = this.getFilters(event);
    const processedPayload = this.buildRequestPayload(
      'purchaseRequestList',
      'tasPurchaserequestDetailId',
      filters,
      1,
    );
    processedPayload.export = true;
    processedPayload.exportType = 'xlsx';
    this.listService.newListPage(processedPayload, `Purchase_Requests_Export_${new Date().toISOString()}.xlsx`, true).subscribe((data: any) => {
    });
  }

  searchPurchaseRequests(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20) {
    if (startingPageNumber == 1) {
      this.purchaseRequests = [];
    }
    const processedPayload = this.buildRequestPayload(
      'purchaseRequestList',
      'tasPurchaserequestDetailId',
      filters,
      startingPageNumber,
      recordsPerPage
    );
    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const records = data.responsedata;

        if (isChequeLookUp) {
          if (records && records?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
        else {
          if (startingPageNumber > 1) {
            records.forEach((r: any) => r.totalAmountDisplay = this.formatCurrency(r.totalAmount));
            this.purchaseRequests = [...this.purchaseRequests, ...records];
          } else {
            records.forEach((r: any) => r.totalAmountDisplay = this.formatCurrency(r.totalAmount));
            this.purchaseRequests = records;
          }

          if (this.childComponent) {
            this.childComponent.recordsPerPage = recordsPerPage;
            this.childComponent.resetPagination(startingPageNumber, currentPage);
          }

          if (this.purchaseRequests?.length) {
            this.chequeNumbers = Array.from(
              new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber)));
          }
        }
      });
  }

  private buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20) {
    if (searchParams.length == 0) {
      //handle the error here
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };

    return payload;
  }

  onPageLoad(event: any): void {
    this.searchPurchaseRequests(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
  }

  onAction(event: any) {
    if (event?.action === 'view') {
      this.viewPurchaseRequestData = event?.data;
      this.openPurchaseRequest(this.isReadOnly ? 'view' : 'actions');
    }
  }

  openConfirmationPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      showOkButton: true,
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showButtons: false,
    };

    this.addDialogId = this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  handleCancel(event: any) {
    if (event?.isCloseTriggeredByDialog) {
      this.dialogSvc.closeDialog(this.addDialogId);
    }
  }

  addNewPurchaseRequest() {
    this.openPurchaseRequest('add');
  }

  openPurchaseRequest(mode: 'add' | 'edit' | 'view' | 'actions') {
    this.mode = mode;
    const dialogConfig: DialogConfig = {
      title: mode === 'add' ? 'Add Purchase Request' : 'View Purchase Request',
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false
    };

    this.addViewPurchaseRequestDialogId = this.dialogSvc.openDialog(
      this.mode === 'add' ? this.createPurchaseRequestDialog : this.viewPurchaseRequestDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  closeAddPurchaseRequestDialog() {
    if (this.addViewPurchaseRequestDialogId) {
      this.dialogSvc.closeDialog(this.addViewPurchaseRequestDialogId);
      this.addViewPurchaseRequestDialogId = null;
      this.viewPurchaseRequestData = null;
    }
  }

  handleCreatePurchaseRequest() {
    this.closeAddPurchaseRequestDialog();
    this.searchPurchaseRequests(this.appliedFilters, undefined, undefined, false, this.childComponent?.recordsPerPage ?? 20);
  }

  handleAddPurchaseRequestCancel(event: any) {
    if (event?.isCloseTriggeredByDialog) {
      this.closeAddPurchaseRequestDialog();
    }
  }
}
